/* components/patterns/charts/ChartLegend.jsx — легенда серий графика.

   Общая для StackedBarChart и LineChartMulti (раньше у них были два одинаковых
   блока `.linechart__legend`). Чип серии — переключатель: нажат = серия на графике.

   Зачем «Скрыть все / Показать все». У почасового графика в разбивке по категориям
   их бывает двадцать, и чтобы посмотреть одну, приходилось выключить девятнадцать
   по одной. Теперь — «Скрыть все» и включить нужную. Кнопки появляются от четырёх
   серий: при двух-трёх это быстрее сделать чипами, и лишние кнопки — только шум.
   Неактуальная кнопка выключена (всё и так показано / всё и так скрыто), а не
   прячется: ряд не прыгает под пальцем.

   Длинная легенда сворачивается тем же правилом, что ExpandableList: первые десять
   чипов, разворот — только если прятать больше двух. Подпись разворота — «Вся
   легенда», а не «Показать все»: рядом стоит «Показать все» про СЕРИИ, и две
   одинаковые подписи с разным смыслом в одном ряду читались бы как одна.

   Контракт — work/analytics-pack-oct/contracts/ui-components.md, раздел 2. */
import React from 'react';
import { Icon } from '../../ui/Icon.jsx';
import { EXPAND_LIMIT, useExpandable } from '../ExpandableList.jsx';

/** С какого числа серий в легенде есть «Скрыть все / Показать все». */
export const LEGEND_BULK_MIN = 4;

/** Чистое состояние легенды: нужны ли кнопки «все», всё ли скрыто, всё ли показано.
 *  Скрытые id, которых уже нет среди серий (сменили метрику), не учитываются. */
export function legendState(series, hidden) {
  const list = series || [];
  const h = hidden || {};
  const off = list.filter((s) => h[s.id]).length;
  return {
    bulk: list.length >= LEGEND_BULK_MIN,
    allHidden: list.length > 0 && off === list.length,
    allShown: off === 0,
  };
}

/**
 * series    — [{ id, name, color }];
 * hidden    — { [id]: true } скрытых серий;
 * onToggle(id), onHideAll(), onShowAll() — действия графика (состояние держит он);
 * focusRef  — ref, куда график возвращает фокус после «Показать все»: «Скрыть все»,
 *             а если кнопок «все» нет (меньше четырёх серий) — первый чип.
 */
export function ChartLegend({ series = [], hidden = {}, onToggle, onHideAll, onShowAll, limit = EXPAND_LIMIT, focusRef }) {
  const list = series || [];
  const { shown, open, toggle, collapsible } = useExpandable(list.length, limit);
  if (!list.length) return null;
  const h = hidden || {};
  const st = legendState(list, h);
  return (
    <div className="linechart__legend chart-legend" role="group" aria-label="Легенда графика">
      {list.slice(0, shown).map((s, i) => {
        const off = !!h[s.id];
        return (
          <button type="button" key={s.id} className={'lc-leg' + (off ? ' is-off' : '')}
            ref={!st.bulk && i === 0 ? focusRef : undefined}
            aria-pressed={!off} onClick={() => onToggle && onToggle(s.id)}>
            <span className="lc-leg__dot" style={{ background: s.color }} />
            <span className="lc-leg__name">{s.name}</span>
          </button>
        );
      })}
      {collapsible && (
        <button type="button" className="chart-legend__more" aria-expanded={open} onClick={toggle}>
          <Icon name={open ? 'chevron-up' : 'chevron-down'} size={12} />
          {/* текст одним элементом — иначе в inline-flex тире отрывается от числа */}
          <span>
            {open
              ? <>Свернуть до первых <span className="num">{limit}</span></>
              : <>Вся легенда — <span className="num">{list.length}</span></>}
          </span>
        </button>
      )}
      {st.bulk && (
        <span className="chart-legend__bulk">
          <button type="button" className="chart-legend__act" ref={focusRef} disabled={st.allHidden} onClick={onHideAll}>
            <Icon name="eye-off" size={13} /><span>Скрыть все</span>
          </button>
          <button type="button" className="chart-legend__act" disabled={st.allShown} onClick={onShowAll}>
            <Icon name="eye" size={13} /><span>Показать все</span>
          </button>
        </span>
      )}
    </div>
  );
}
